import ImmutablePropTypes from 'react-immutable-proptypes'
import React, { useState, useCallback } from 'react'
import { compose } from 'recompose'
import PropTypes from 'prop-types'
import { Map } from 'immutable'
import AmbassadorsYearDropdown from '../AmbassadorsYearDropdown/index'
import { connect as connectStaticText } from '../../StaticText/connect'
import { formatAmount, PAYOUT_MIN } from '../../../services/ambassadors/index'
import { exportCSVFile } from '../../../services/file/index'
import { format } from '../../../services/date-time/index'

/**
 * Create the csv headers
 * @param {Map} staticText The staticText
 * @returns object with headers
 */
function createCsvHeaders (staticText) {
  return {
    month: staticText.getIn(['data', 'month']),
    unpaidFromPrevious: staticText.getIn(['data', 'unpaidFromPrevious']),
    monthlyEarning: staticText.getIn(['data', 'monthlyEarning']),
    total: staticText.getIn(['data', 'totalEarned']),
  }
}

/**
 * Create the csv data
 * @param {Map} payouts The payouts
 * @returns array of csv row data objects
 */
function createCsvData (payouts, locale, staticText) {
  return payouts.map((payout) => {
    const active = payout.get('ambassadorIsActive')
    return {
      month: staticText.getIn(['data', format(payout.get('endDate'), locale, 'MMMM')]),
      unpaidFromPrevious: formatAmount({ amount: payout.get('unpaidFromPreviousMonthUSD'), active, useSymbol: false }),
      monthlyEarning: formatAmount({ amount: payout.get('currentMonthCommissionUSD'), active, useSymbol: false }),
      total: formatAmount({ amount: payout.get('totalPayoutUSD'), active, useSymbol: false }),
    }
  }).toJS()
}

/**
 * Create the csv title
 * @param {Map} staticText The staticText
 * @returns string with csv title
 */
function createCsvTitle (staticText, locale) {
  return `${staticText.getIn(['data', 'csvTitle'])}-${format(new Date(), locale, 'YYYY-MM-DD')}`
}

/**
 * Create the yearly total
 * @param {Map} payouts The payouts
 * @returns formatted total
 */
function getYearlyTotal (payouts) {
  const total = payouts.reduce((acc, payout) => {
    return payout.get('ambassadorIsActive') ? payout.get('currentMonthCommissionUSD') + acc : acc
  }, 0)
  return formatAmount({ amount: total })
}
const date = new Date()
const currentYear = date.getFullYear()

/**
 * Handle the csv download
 * @param {Object} headers The csv headers
 * @param {Array} data The csv data
 * @param {String} title The csv file name
 */
function onSubmitCsv (headers, data, title) {
  return exportCSVFile(headers, data, title)
}

/**
 * Get the correct classes given the amount
 * @param {number} amount
 * @returns {String}
 */
function getClassFn (baseClass) {
  return ({ amount, active }) => {
    const base = [baseClass]
    if (active && (amount && amount < PAYOUT_MIN)) {
      base.push(`${baseClass}--alert`)
    }
    return base.join(' ')
  }
}

/**
 * Get the corresponding cells for the row.
 * @param {Object} row The row
 * @param {Map} staticText The staticText
 * @param {String} locale The locale
 */
function renderRow (row, staticText, locale) {
  const active = row.get('ambassadorIsActive')
  const getTableCellClass = getClassFn('ambassadors-payouts__table-cell')
  const totalPaidAndUnpaid = row.get('totalPayoutUSD') + row.get('totalUnpaidUSD')

  return (
    <tr className="ambassadors-payouts__table-row" key={row.get('uuid')}>
      <td className="ambassadors-payouts__table-cell">
        {staticText.getIn(['data', format(row.get('endDate'), locale, 'MMMM')])}
      </td>
      <td className="ambassadors-payouts__table-cell">
        {formatAmount({ amount: row.get('unpaidFromPreviousMonthUSD'), active })}
      </td>
      <td className="ambassadors-payouts__table-cell">
        {formatAmount({ amount: row.get('currentMonthCommissionUSD'), active })}
      </td>
      <td className={getTableCellClass({ amount: totalPaidAndUnpaid, active })}>
        {formatAmount({ amount: totalPaidAndUnpaid, active })}
      </td>
    </tr>
  )
}

/**
 * Ambassadors Payouts
 * @param {Object} props The props
 * @returns The complete AmbassadorsPayouts component
 */
const AmbassadorsPayouts = (props) => {
  const {
    getAmbassadorPayouts,
    staticText,
    payouts,
    locale,
  } = props

  const includesCurrentMonth = !!(payouts.find((payout) => {
    return format(new Date(), locale, 'MMMM') === format(new Date(payout.get('endDate')), locale, 'MMMM')
  }))
  const allPayouts = includesCurrentMonth ? payouts : payouts.unshift(Map({
    unpaidFromPreviousMonthUSD: null,
    currentMonthCommissionUSD: null,
    ambassadorIsActive: undefined,
    totalPayoutUSD: null,
    endDate: new Date(),
    uuid: 'na',
  }))
  const [selectedYear, setSelectedYear] = useState(currentYear)
  const changeYear = useCallback((year) => {
    getAmbassadorPayouts(year)
    setSelectedYear(year)
  }, [selectedYear])

  const renderDesktopTable = (data) => {
    return (
      <table className="ambassadors-payouts__table">
        <thead className="ambassadors-payouts__table-header">
          <tr>
            <th className="ambassadors-payouts__table-header-cell">{staticText.getIn(['data', 'month'])}</th>
            <th className="ambassadors-payouts__table-header-cell">{staticText.getIn(['data', 'unpaidFromPrevious'])}</th>
            <th className="ambassadors-payouts__table-header-cell">{staticText.getIn(['data', 'monthlyEarning'])}</th>
            <th className="ambassadors-payouts__table-header-cell">{staticText.getIn(['data', 'totalEarned'])}</th>
          </tr>
        </thead>
        <tbody className="ambassadors-payouts__table-body">
          {data.map((r) => renderRow(r, staticText, locale))}
        </tbody>
        <tfoot>
          <tr>
            <td className="ambassadors-payouts__table-cell--foot">
              {currentYear} {staticText.getIn(['data', 'total'])}
            </td>
            <td className="ambassadors-payouts__table-cell" />
            <td className="ambassadors-payouts__table-cell--foot" >
              {getYearlyTotal(allPayouts)}
            </td>
            <td className="ambassadors-payouts__table-cell" />
          </tr>
        </tfoot>
      </table>
    )
  }

  const renderMobileBlock = (row) => {
    const active = row.get('ambassadorIsActive')
    const getClass = getClassFn('ambassadors-payouts__mobile-table-value')
    const totalPaidAndUnpaid = row.get('totalPayoutUSD') + row.get('totalUnpaidUSD')
    return (
      <div className="ambassadors-payouts__mobile-table-block" key={row.get('uuid')}>
        <div className="ambassadors-payouts__mobile-table-row">
          <div className="ambassadors-payouts__mobile-table-head">
            {staticText.getIn(['data', 'month'])}
          </div>
          <div className="ambassadors-payouts__mobile-table-value">
            {staticText.getIn(['data', format(row.get('endDate'), locale, 'MMMM')])}
          </div>
        </div>
        <div className="ambassadors-payouts__mobile-table-row">
          <div className="ambassadors-payouts__mobile-table-head">
            {staticText.getIn(['data', 'unpaidFromPrevious'])}
          </div>
          <div className="ambassadors-payouts__mobile-table-value">
            {formatAmount({ amount: row.get('unpaidFromPreviousMonthUSD'), active })}
          </div>
        </div>
        <div className="ambassadors-payouts__mobile-table-row">
          <div className="ambassadors-payouts__mobile-table-head">
            {staticText.getIn(['data', 'monthlyEarning'])}
          </div>
          <div className="ambassadors-payouts__mobile-table-value">
            {formatAmount({ amount: row.get('currentMonthCommissionUSD'), active })}
          </div>
        </div>
        <div className="ambassadors-payouts__mobile-table-row">
          <div className="ambassadors-payouts__mobile-table-head">
            {staticText.getIn(['data', 'totalEarned'])}
          </div>
          <div className={getClass({ amount: totalPaidAndUnpaid, active })}>
            {formatAmount({ amount: totalPaidAndUnpaid, active })}
          </div>
        </div>
      </div>
    )
  }

  const renderMobileTable = (data) => {
    return (
      <div className="ambassadors-payouts__mobile-table">
        <div className="ambassadors-payouts__mobile-table-top">
          <div className="ambassadors-payouts__mobile-table-row">
            <div className="ambassadors-payouts__mobile-table-foot">
              {currentYear} {staticText.getIn(['data', 'total'])}
            </div>
            <div className="ambassadors-payouts__mobile-table-foot">
              {getYearlyTotal(allPayouts)}
            </div>
          </div>
        </div>
        {data.map((r) => (
          renderMobileBlock(r)
        ))}
      </div>
    )
  }

  return (
    <div className="ambassadors-payouts">
      <div className="ambassadors-payouts__selector-container">
        <AmbassadorsYearDropdown
          onSubmit={() => onSubmitCsv(
            createCsvHeaders(staticText),
            createCsvData(payouts, locale, staticText),
            createCsvTitle(staticText, locale),
          )}
          onChange={changeYear}
        />
      </div>
      <div className="ambassadors-payouts__table-container">
        {payouts.size > 0 ? renderDesktopTable(allPayouts) : staticText.getIn(['data', 'noData']).replace(/\$\{ year \}/g, selectedYear)}
      </div>
      <div className="ambassadors-payouts__mobile-table-container">
        {payouts.size > 0 ? renderMobileTable(allPayouts) : staticText.getIn(['data', 'noData']).replace(/\$\{ year \}/g, selectedYear)}
      </div>
      <div className="ambassadors-payouts__copy-container">
        <div className="ambassadors-payouts__copy--bullet">{staticText.getIn(['data', 'copy1']).replace(/\$\{ payoutMin \}/g, PAYOUT_MIN)}</div>
        <div className="ambassadors-payouts__copy">{staticText.getIn(['data', 'copy2'])}</div>
      </div>
    </div>
  )
}

AmbassadorsPayouts.propTypes = {
  getAmbassadorPayouts: PropTypes.func.isRequired,
  staticText: ImmutablePropTypes.map.isRequired,
  payouts: ImmutablePropTypes.list.isRequired,
  locale: PropTypes.string.isRequired,
}

export default compose(
  connectStaticText({ storeKey: 'ambassadorsPayouts' }),
)(AmbassadorsPayouts)
